import { makeAutoObservable, runInAction } from 'mobx';
import { logger } from '@cutwise/utils';

import { CLICK_SHARE, OPEN_QR_CODE } from '@/analytics/services/Analytics';
import AnalyticsStore from '@/analytics/store/AnalyticsStore';
import useStore from '@/common/hooks/useStore';
import Notifier from '@/common/services/Notifier';
import { DriveAPI } from '@/drive/api/DriveAPI';
import { DriveItemsResponse } from '@/drive/entities/DriveItemsResponse';
import { DriveType } from '@/drive/entities/DriveType';
import { DriveItemRequest } from '@/drive/types/DriveItemRequest';
import { ShareActionType, SocialShareActionType } from '@/forwarding/constants/ShareActionType';
import { ShareConfig } from '@/forwarding/entities/ShareConfig/ShareConfig';
import { ForwardNotificationService } from '@/forwarding/services/ForwardNotificationService';
import { cleanUpForm, ForwardForm } from '@/forwarding/types/ForwardForm';
import { NativeShareConfig } from '@/forwarding/types/NativeShareConfig';
import { createMailUrl } from '@/forwarding/utils/createMailUrl';
import { PreviewService } from '@/media/services/ProductPreviewService';
import UserStore from '@/user/stores/UserStore';

class ForwardShareStore {
  isSubmitting = false;

  isQrShown = false;

  config: Nullable<ShareConfig> = null;

  private nativeShareAbortController: Nullable<AbortController> = null;

  constructor(
    private userStore: UserStore,
    private analyticsStore: AnalyticsStore,
    private spectrumDriveAPI = new DriveAPI(DriveType.CutwiseSpectrum),
  ) {
    makeAutoObservable<ForwardShareStore, 'baseAddToSpectrum' | 'nativeShareAbortController'>(this, {
      baseAddToSpectrum: false,
      nativeShareAbortController: false,
    });
  }

  get actions(): ShareActionType[] {
    const config = this.config;

    if (!config) {
      return [];
    }

    const actions: ShareActionType[] = [];

    if (config.getSocialShareUrl(ShareActionType.WhatsApp)) {
      actions.push(ShareActionType.WhatsApp);
    }

    if (config.getAddToSpectrumRequests()) {
      actions.push(ShareActionType.AddToSpectrum);
    }

    actions.push(ShareActionType.CopyLink, ShareActionType.CopyLinkWithInfo);

    if (config.getMailUrlParams()) {
      actions.push(ShareActionType.Email);
    }

    if (config.getUrl()) {
      actions.push(ShareActionType.QrCode);
    }

    const socialActions: SocialShareActionType[] = [
      ShareActionType.Facebook,
      ShareActionType.Twitter,
      ShareActionType.Pinterest,
    ];

    actions.push(...socialActions.filter((type) => !!config.getSocialShareUrl(type)));

    if (config.getNativeShareConfig() && 'share' in navigator) {
      actions.push(ShareActionType.NativeShare);
    }

    return actions;
  }

  get hasMultipleUrls(): boolean {
    return !!this.config?.hasMultipleUrls;
  }

  refresh = (config: ShareConfig) => {
    this.config = config;
    this.isQrShown = false;
  };

  reset = () => {
    this.config = null;
    this.isQrShown = false;
    this.nativeShareAbortController?.abort();
    this.nativeShareAbortController = null;
  };

  hideQr = () => {
    this.isQrShown = false;
  };

  execute = async (action: ShareActionType, form: ForwardForm): Promise<Nullable<DriveItemsResponse>> => {
    const config = this.config;

    if (!config) {
      return null;
    }

    switch (action) {
      case ShareActionType.AddToSpectrum:
        return this.addConfigToSpectrum(config, form);
      case ShareActionType.CopyLink:
        await this.copyToClipboard(config.getCopyText());

        break;
      case ShareActionType.CopyLinkWithInfo:
        await this.copyToClipboard(config.getInfoText());

        break;
      case ShareActionType.Email:
        this.openMail(config);

        break;
      case ShareActionType.QrCode:
        this.showQr(config);

        break;
      case ShareActionType.WhatsApp:
      case ShareActionType.Facebook:
      case ShareActionType.Twitter:
      case ShareActionType.Pinterest:
        this.shareToSocial(action, config);

        break;
      case ShareActionType.NativeShare:
        await this.nativeShare(config);

        break;
    }

    return null;
  };

  addToSpectrum = async (requests: DriveItemRequest[], form: ForwardForm): Promise<DriveItemsResponse> => {
    this.isSubmitting = true;

    try {
      return await this.baseAddToSpectrum(requests, form);
    } finally {
      runInAction(() => {
        this.isSubmitting = false;
      });
    }
  };

  private addConfigToSpectrum(config: ShareConfig, form: ForwardForm): Promise<Nullable<DriveItemsResponse>> {
    const requests = config.getAddToSpectrumRequests();

    if (requests) {
      return this.addToSpectrum(requests, form);
    } else {
      logger.error('addToSpectrum called for object which could not be added to spectrum');

      return Promise.resolve(null);
    }
  }

  private baseAddToSpectrum = async (requests: DriveItemRequest[], form: ForwardForm): Promise<DriveItemsResponse> => {
    const mappedReqData = requests.map((item) => ({
      inheritAccess: false,
      ...item,
      ...cleanUpForm(form),
      parent: null,
    }));

    const res = await this.spectrumDriveAPI.batchCreate(mappedReqData);

    if (res.hasErrors()) {
      res.getErrors().forEach((error) => Notifier.error(error));
    } else {
      ForwardNotificationService.addedToSpectrumSuccess(res.getItems()[0].id);
    }

    return res;
  };

  private async copyToClipboard(value: string) {
    await navigator.clipboard.writeText(value);
    Notifier.success('Copied to Clipboard');
  }

  private openMail(config: ShareConfig) {
    const mailUrlParams = config.getMailUrlParams(this.userStore.user?.email);

    if (mailUrlParams) {
      window.location.assign(createMailUrl(mailUrlParams));
    }
  }

  private showQr(config: ShareConfig) {
    this.isQrShown = true;
    this.analyticsStore.sendEventWithContext(OPEN_QR_CODE, config.getAnalyticsDimensions() ?? {});
  }

  private shareToSocial(type: SocialShareActionType, config: ShareConfig) {
    const url = config.getSocialShareUrl(type);

    if (url) {
      this.analyticsStore.sendEventWithContext(CLICK_SHARE, config.getAnalyticsDimensions() ?? {});
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  private async nativeShare(config: ShareConfig) {
    const shareConfig = config.getNativeShareConfig();

    if (!shareConfig) {
      return;
    }

    this.analyticsStore.sendEventWithContext(CLICK_SHARE, config.getAnalyticsDimensions() ?? {});

    try {
      await this.shareNatively(shareConfig);
    } catch (error) {
      const shareCancelled = (error as Error)?.name === 'AbortError';

      if (!shareCancelled) {
        logger.error('Failed to share', error);
      }
    }
  }

  private async shareNatively(shareConfig: NativeShareConfig) {
    const previewUrl = shareConfig.previewUrl;

    if (!previewUrl) {
      await navigator.share(shareConfig);

      return;
    }

    this.nativeShareAbortController = new AbortController();

    const previewFile = await PreviewService.fetchPreviewFile(previewUrl, this.nativeShareAbortController.signal);

    if (previewFile && navigator.canShare && navigator.canShare({ files: [previewFile] })) {
      await navigator.share({ ...shareConfig, files: [previewFile] });
    } else {
      await navigator.share(shareConfig);
    }
  }
}

export const useForwardShareStore = () => useStore(ForwardShareStore);

export default ForwardShareStore;
